import "./labelWorkspace.css";

import Money from "@dashboard/components/Money";
import { useLabelVariantSearchQuery } from "@dashboard/graphql";
import { isOperatorHost } from "@dashboard/operator/operatorHost";
import { Box, Button, Text } from "@saleor/macaw-ui-next";
import { Plus, Search } from "lucide-react";
import { KeyboardEvent, useEffect, useMemo, useRef, useState } from "react";
import { useIntl } from "react-intl";

import { LabelThumbnail } from "./LabelThumbnail";
import { isExactSkuQuery, narrowVariantsToQuery, variantMatchesAllTerms } from "./variantSearch";
import { messages } from "./views/LabelPrintingOverview/messages";

// Everything the queue needs to show a variant before its full label data
// (images, storefront URL) has loaded.
export interface LabelVariantHit {
  variantId: string;
  productId: string;
  productName: string;
  variantName: string;
  sku: string;
  batchNumber: string | null;
  price: { amount: number; currency: string } | null;
  thumbnailUrl: string | null;
}

interface HitGroup {
  productId: string;
  productName: string;
  hits: LabelVariantHit[];
  // Every variant the backend returned for the product, before narrowing.
  allHits?: LabelVariantHit[];
}

interface LabelVariantSearchProps {
  queuedCounts: Record<string, number>;
  onAdd: (hit: LabelVariantHit) => void;
  onAddMany: (hits: LabelVariantHit[]) => void;
  // Arriving from a product page: its variants, offered while the box is
  // empty so the wanted one is a click away instead of a search.
  pickGroup?: HitGroup | null;
}

const SEARCH_DEBOUNCE_MS = 250;
const SEARCH_PAGE_SIZE = 60;
// Ops shows only literal matches, best-ranked first, and at most this many.
const OPS_MAX_RESULTS = 5;

// Keeps only hits containing every query term and caps the total, preserving
// the engine's ranking order across products.
const toStrictTopHits = (groups: HitGroup[], query: string, limit: number): HitGroup[] => {
  let remaining = limit;

  return groups
    .map(group => {
      const hits = group.hits
        .filter(hit =>
          variantMatchesAllTerms(group.productName, { name: hit.variantName, sku: hit.sku }, query),
        )
        .slice(0, Math.max(remaining, 0));

      remaining -= hits.length;

      return { ...group, hits };
    })
    .filter(group => group.hits.length > 0);
};

export const LabelVariantSearch = ({
  queuedCounts,
  onAdd,
  onAddMany,
  pickGroup,
}: LabelVariantSearchProps) => {
  const intl = useIntl();
  const strictSearch = useMemo(() => isOperatorHost(), []);
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  // A barcode scanner types the SKU and presses Enter faster than the
  // debounce fires; remember the Enter so the exact match is added as soon
  // as its results arrive.
  const [pendingEnterFor, setPendingEnterFor] = useState<string | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(query.trim());
      setActiveIndex(0);
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [query]);

  // "/" jumps to the search box from anywhere on the page.
  useEffect(() => {
    const onKeyDown = (event: globalThis.KeyboardEvent) => {
      const target = event.target as HTMLElement | null;

      if (
        event.key === "/" &&
        !event.metaKey &&
        !event.ctrlKey &&
        !target?.closest("input, textarea, select, [contenteditable='true']")
      ) {
        event.preventDefault();
        inputRef.current?.focus();
      }
    };

    document.addEventListener("keydown", onKeyDown);

    return () => document.removeEventListener("keydown", onKeyDown);
  }, []);

  const { data, previousData, loading } = useLabelVariantSearchQuery({
    variables: { query: debouncedQuery, first: SEARCH_PAGE_SIZE },
    skip: !debouncedQuery,
  });
  const resultData = data ?? (loading ? previousData : undefined);

  const groups = useMemo<HitGroup[]>(() => {
    if (!debouncedQuery) {
      return [];
    }

    const byProduct = new Map<string, HitGroup>();

    for (const edge of resultData?.productVariantSearch?.edges ?? []) {
      const variant = edge.node;
      const price = variant.channelListings?.[0]?.price;
      const group = byProduct.get(variant.product.id) ?? {
        productId: variant.product.id,
        productName: variant.product.name,
        hits: [],
      };

      group.hits.push({
        variantId: variant.id,
        productId: variant.product.id,
        productName: variant.product.name,
        variantName: variant.name,
        sku: variant.sku ?? "",
        batchNumber: variant.latestBatchNumber ?? null,
        price: price ? { amount: price.amount, currency: price.currency } : null,
        thumbnailUrl: variant.product.thumbnail?.url ?? null,
      });
      byProduct.set(variant.product.id, group);
    }

    const productGroups = Array.from(byProduct.values());

    if (strictSearch) {
      return toStrictTopHits(
        productGroups.map(group => ({ ...group, allHits: group.hits })),
        debouncedQuery,
        OPS_MAX_RESULTS,
      );
    }

    return productGroups.map(group => ({
      ...group,
      allHits: group.hits,
      hits: narrowVariantsToQuery(
        group.productName,
        group.hits.map(hit => ({ ...hit, name: hit.variantName })),
        debouncedQuery,
      ),
    }));
  }, [resultData, debouncedQuery, strictSearch]);

  const visibleGroups = query.trim() ? groups : pickGroup ? [pickGroup] : [];
  const flatHits = visibleGroups.flatMap(group => group.hits);
  const exactHit =
    flatHits.length === 1 && isExactSkuQuery(debouncedQuery, flatHits[0].sku) ? flatHits[0] : null;
  const isStale = query.trim() !== debouncedQuery || loading;

  const clear = () => {
    setQuery("");
    setDebouncedQuery("");
    setActiveIndex(0);
  };

  const addHit = (hit: LabelVariantHit, clearAfter = false) => {
    onAdd(hit);

    if (clearAfter) {
      clear();
      inputRef.current?.focus();
    }
  };

  useEffect(() => {
    if (!pendingEnterFor || pendingEnterFor !== debouncedQuery || loading || !data) {
      return;
    }

    setPendingEnterFor(null);

    if (exactHit) {
      addHit(exactHit, true);
    }
    // Only reacts to the results arriving for the query Enter was pressed on.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, loading, debouncedQuery, pendingEnterFor]);

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveIndex(index => Math.min(flatHits.length - 1, index + 1));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex(index => Math.max(0, index - 1));
    } else if (event.key === "Escape") {
      clear();
    } else if (event.key === "Enter") {
      event.preventDefault();

      const trimmed = query.trim();

      if (!trimmed) {
        return;
      }

      // Results still belong to an older query: wait for this one, and only
      // add automatically if it turns out to be an exact SKU.
      if (isStale) {
        setDebouncedQuery(trimmed);
        setPendingEnterFor(trimmed);

        return;
      }

      if (exactHit) {
        addHit(exactHit, true);
      } else if (flatHits[activeIndex]) {
        addHit(flatHits[activeIndex]);
      }
    }
  };

  let rowIndex = -1;

  return (
    <Box display="flex" flexDirection="column" gap={3}>
      <Box
        as="label"
        className="labelSearchBox"
        display="flex"
        alignItems="center"
        gap={2}
        paddingX={3}
        borderWidth={1}
        borderStyle="solid"
        borderColor="default1"
        borderRadius={3}
        backgroundColor="default1"
      >
        <Search size={16} aria-hidden />
        <input
          ref={inputRef}
          type="search"
          autoComplete="off"
          autoFocus
          aria-label={intl.formatMessage(messages.searchPlaceholder)}
          placeholder={intl.formatMessage(messages.searchPlaceholder)}
          value={query}
          onChange={event => setQuery(event.target.value)}
          onKeyDown={handleKeyDown}
          data-test-id="label-variant-search"
        />
        <Text
          size={1}
          color="default2"
          borderWidth={1}
          borderStyle="solid"
          borderColor="default1"
          borderRadius={2}
          paddingX={1}
          aria-hidden
        >
          /
        </Text>
      </Box>

      {query.trim() && !isStale && flatHits.length === 0 && (
        <Text size={2} color="default2" textAlign="center" padding={4}>
          {intl.formatMessage(messages.searchNoResults, { query: query.trim() })}
        </Text>
      )}
      {query.trim() && isStale && flatHits.length === 0 && (
        <Text size={2} color="default2" padding={2}>
          {intl.formatMessage(messages.searchLoading)}
        </Text>
      )}

      {flatHits.length > 0 && (
        <Box
          borderWidth={1}
          borderStyle="solid"
          borderColor="default1"
          borderRadius={3}
          overflow="hidden"
          role="listbox"
          style={{ opacity: isStale && query.trim() ? 0.6 : 1 }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            gap={2}
            paddingX={3}
            paddingY={1.5}
            backgroundColor="default2"
            flexWrap="wrap"
          >
            <Text size={1} color="default2">
              {!query.trim() && pickGroup ? (
                intl.formatMessage(messages.pickVariantTitle, { product: pickGroup.productName })
              ) : exactHit ? (
                <>
                  <Text
                    size={1}
                    fontWeight="bold"
                    color="success1"
                    backgroundColor="success1"
                    paddingX={1.5}
                    borderRadius={2}
                  >
                    {intl.formatMessage(messages.searchExactSku)}
                  </Text>{" "}
                  {intl.formatMessage(messages.searchEnterAdds)}
                </>
              ) : (
                intl.formatMessage(messages.searchResultCount, {
                  variants: flatHits.length,
                  products: visibleGroups.length,
                })
              )}
            </Text>
            <Text size={1} color="default2">
              {intl.formatMessage(messages.searchKeyboardHint)}
            </Text>
          </Box>
          {visibleGroups.map(group => (
            <Box
              key={group.productId}
              borderTopWidth={1}
              borderTopStyle="solid"
              borderColor="default1"
            >
              <Box display="flex" alignItems="center" gap={2} paddingX={3} paddingTop={2}>
                <Text size={2} fontWeight="bold" style={{ flex: 1, minWidth: 0 }}>
                  {group.productName}
                </Text>
                {(group.allHits ?? group.hits).length > 1 && (
                  <Button
                    variant="tertiary"
                    size="small"
                    onClick={() => onAddMany(group.allHits ?? group.hits)}
                  >
                    {intl.formatMessage(messages.addAllVariants, {
                      count: (group.allHits ?? group.hits).length,
                    })}
                  </Button>
                )}
              </Box>
              {group.hits.map(hit => {
                rowIndex += 1;

                const index = rowIndex;
                const queued = queuedCounts[hit.variantId];

                return (
                  <Box
                    key={hit.variantId}
                    className="labelSearchRow"
                    data-active={index === activeIndex}
                    role="option"
                    aria-selected={index === activeIndex}
                    display="grid"
                    alignItems="center"
                    gap={3}
                    paddingX={3}
                    paddingY={1.5}
                    style={{ gridTemplateColumns: "40px minmax(0, 1fr) auto auto" }}
                    onMouseEnter={() => setActiveIndex(index)}
                    onClick={() => addHit(hit)}
                    data-test-id="label-search-result"
                  >
                    <LabelThumbnail url={hit.thumbnailUrl} />
                    <Box style={{ minWidth: 0 }}>
                      <Text size={2} fontWeight="bold" display="block">
                        {hit.variantName || intl.formatMessage(messages.productLabel)}
                      </Text>
                      <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
                        <Text size={1} color="default2" style={{ fontFamily: "monospace" }}>
                          {hit.sku}
                        </Text>
                        {hit.batchNumber && <BatchBadge code={hit.batchNumber} />}
                        {queued ? (
                          <Text size={1} color="accent1">
                            {intl.formatMessage(messages.inQueue, { count: queued })}
                          </Text>
                        ) : null}
                      </Box>
                    </Box>
                    <Text size={2} style={{ fontVariantNumeric: "tabular-nums" }}>
                      {hit.price ? <Money money={hit.price} /> : null}
                    </Text>
                    <Button
                      variant="secondary"
                      size="small"
                      tabIndex={-1}
                      icon={queued ? undefined : <Plus size={14} />}
                      onClick={event => {
                        event.stopPropagation();
                        addHit(hit);
                      }}
                    >
                      {intl.formatMessage(queued ? messages.addOneMore : messages.add)}
                    </Button>
                  </Box>
                );
              })}
              <Box paddingBottom={1} />
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export const BatchBadge = ({ code }: { code: string }) => (
  <Text
    size={1}
    borderWidth={1}
    borderStyle="solid"
    borderColor="default1"
    borderRadius={2}
    paddingX={1}
    backgroundColor="default2"
    style={{ fontFamily: "monospace" }}
  >
    {code}
  </Text>
);
